import type { LayoutRecord } from "@/shared/domain/layouts/LayoutRecord";
import { RobotoNotFoundException } from "@/shared/http/exceptions";
import { Goto } from "@/shared/navigation";
import type {
  ApplyLayoutResult,
  WorkspaceActionPayload,
} from "@/shared/services/AiAgentThreadPubSubBus/models";

import { ApprovalMode } from "../approvalModes";
import type { ClientToolDeps, ClientToolExecutionResult } from "../ClientTools";
import { createClientTool, toolError, toolSuccess } from "../ClientTools";

import { ApplyLayoutConfirmation } from "./confirmations";
import {
  markNavigatedToVisualizer,
  publishWhenVisualizerReady,
  VISUALIZER_NOT_READY_ERROR,
  visualizerStatus,
} from "./workspaceActions";

// ---------------------------------------------------------------------------
// Input types
// ---------------------------------------------------------------------------

export interface ApplyLayoutInput {
  layout_id: string;
}

const APPLIED_NOTE =
  "Panels are rebuilt from the layout's rules against the files currently loaded.";

const OPENED_WITHOUT_DATA_NOTE =
  "Opened a new visualizer with this layout active. No data is loaded there, so it shows no panels " +
  "until files are added. To visualize specific files with this layout, call " +
  "client_visualize_open_file first and then this tool.";

function reportOutcome(
  layout: LayoutRecord,
  result: ApplyLayoutResult,
): ClientToolExecutionResult {
  if (!result.applied) {
    return toolError(result.reason);
  }
  return toolSuccess("apply_layout", {
    layout_id: layout.layout_id,
    layout_name: layout.name,
    note: APPLIED_NOTE,
  });
}

// ---------------------------------------------------------------------------
// Execute
// ---------------------------------------------------------------------------

async function execute(
  input: ApplyLayoutInput,
  deps?: ClientToolDeps,
): Promise<ClientToolExecutionResult> {
  if (!deps?.goto) {
    return toolError("Navigation is not available");
  }
  if (!deps.layoutService) {
    return toolError("Layout service is not available");
  }
  if (!deps.orgId) {
    return toolError("Organization is not available");
  }
  if (!input.layout_id) {
    return toolError("layout_id is required");
  }

  let layout: LayoutRecord;
  try {
    layout = await deps.layoutService.getLayoutById(
      input.layout_id,
      deps.orgId,
    );
  } catch (err) {
    if (err instanceof RobotoNotFoundException) {
      return toolError(`Layout '${input.layout_id}' not found`);
    }
    return toolError(err instanceof Error ? err.message : String(err));
  }

  // The workspace answers through onResult, possibly after publish() returns
  // (it holds the layout while its data is still loading). One payload serves
  // every publish attempt: a publish nobody takes hands it to no one, so a
  // retry cannot make onResult fire twice.
  let settle: (result: ApplyLayoutResult) => void = () => {};
  const outcome = new Promise<ApplyLayoutResult>((resolve) => {
    settle = resolve;
  });
  const payload: WorkspaceActionPayload = {
    type: "apply_layout",
    payload: { layout, onResult: (result) => settle(result) },
  };

  switch (visualizerStatus(deps)) {
    case "open": {
      if (!deps.publishWorkspaceAction?.(payload)) {
        return toolError("Failed to publish workspace action");
      }
      return reportOutcome(layout, await outcome);
    }
    case "opening": {
      const publish = deps.publishWorkspaceAction;
      if (!publish) {
        return toolError(VISUALIZER_NOT_READY_ERROR);
      }
      const published = await publishWhenVisualizerReady(
        publish,
        payload,
        deps.batchScope,
      );
      if (!published) {
        return toolError(VISUALIZER_NOT_READY_ERROR);
      }
      return reportOutcome(layout, await outcome);
    }
    case "unreachable":
      return toolError(VISUALIZER_NOT_READY_ERROR);
    case "closed": {
      deps.goto.visualize({ params: { layoutId: layout.layout_id } });
      markNavigatedToVisualizer(deps);
      return toolSuccess("apply_layout", {
        layout_id: layout.layout_id,
        layout_name: layout.name,
        navigated_to: Goto.route.Visualize,
        note: OPENED_WITHOUT_DATA_NOTE,
      });
    }
  }
}

// ---------------------------------------------------------------------------
// Tool module export
// ---------------------------------------------------------------------------

export const tool = createClientTool<ApplyLayoutInput>({
  name: "client_visualize_apply_layout",
  description: `Apply a saved layout to the visualizer workspace the user has open, without navigating away. The layout's rules produce panels only for signals present in the files currently loaded; if nothing is loaded, use client_visualize_open_file first. When the visualizer is not open, navigates to a fresh visualizer that has the layout active but no data loaded, so it shows no panels until files are added.`,
  input_schema: {
    type: "object",
    properties: {
      layout_id: {
        type: "string",
        description:
          "Layout ID (e.g., 'ly_abc123'). The saved layout to apply.",
      },
    },
    required: ["layout_id"],
    additionalProperties: false,
  },
  execute,
  defaultApprovalMode: ApprovalMode.AUTO_EXECUTE,
  allowableApprovalModes: [
    ApprovalMode.REQUIRE_APPROVAL,
    ApprovalMode.FOLLOW_AUTO,
    ApprovalMode.AUTO_EXECUTE,
  ],
  displayName: "Apply Layout",
  displayDescription: "Applies a saved layout to the visualizer.",
  ConfirmationContent: ApplyLayoutConfirmation,
});
